import React, { useState, useCallback, useEffect } from 'react';
import { Form, Row, Col, Button, Tabs, Tab, Alert } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMeter, faEye } from '@fortawesome/pro-solid-svg-icons';
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';

import PlugCustomTemplateUsageRules from './PlugCustomTemplateUsageRules';
import PlugCustomTemplateCostRules from './PlugCustomTemplateCostRules';
import SelectPlugLoadedAccountModal from '../../modals/PlugCustomTemplateModals/SelectPlugLoadedAccountModal';
import ViewPlugLineItemsModal from '../../modals/PlugCustomTemplateModals/ViewPlugLineItemsModal';
import ApiServiceServerless from '../../../xhr_libs';
import { DEFAULT_TEMPLATE, NAME_COUNT_OPTIONS } from './helpers';

export default function PlugCustomTemplate(props) {
  const {
    activeTemplate,
    setTemplates,
    localUtilityAccounts,
    providerAccounts,
    organizationId,
    showToast,
    isSaving,
    setIsSaving,
  } = props;

  const [activeTab, setActiveTab] = useState('template-details');

  const [localTemplate, setLocalTemplate] = useState(DEFAULT_TEMPLATE);
  const [editTemplate, setEditTemplate] = useState(DEFAULT_TEMPLATE);

  const [loadedAccount, setLoadedAccount] = useState(null);
  const [statements, setStatements] = useState([]);
  const [statementIdx, setStatementIdx] = useState(0);
  const [accIdx, setAccIdx] = useState(0);
  const [meterIdx, setMeterIdx] = useState(0);

  const [results, setResults] = useState([]);

  const [templateInUse, setTemplateInUse] = useState(false);

  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);


  useEffect(() => {
    if (localUtilityAccounts.find((acc) => acc.utility_template && acc.utility_template.custom_template_id === activeTemplate.id)) { setTemplateInUse(true) } else { setTemplateInUse(false) }
  }, [localUtilityAccounts, activeTemplate])

  useEffect(() => {
    let temp_template = { ...activeTemplate };

    setLocalTemplate({ ...temp_template });
    setEditTemplate({ ...temp_template });
  }, [activeTemplate]);

  const onFieldChange = useCallback((fieldName, value) => {
    setEditTemplate((ep) => {
      ep[fieldName] = value;
      return { ...ep };
    });
  }, []);

  const handleChangeCount = useCallback((fieldName1, fieldName2, value) => {
    setEditTemplate((ep) => {
      ep[fieldName1] = value;
      let list = [...ep[fieldName2]]
      const diff = value - list.length
      if (value < list.length) {
        list = list.slice(0, value)
      }
      if (value > list.length) {
        for (let idx = 0; idx < diff; idx++) {
          list.push('')
        }
      }
      ep[fieldName2] = list;
      return { ...ep };
    });

  }, [])

  const editFieldName = useCallback((fieldName, idx, value) => {
    setEditTemplate((ep) => {
      let list = [...ep[fieldName]]
      list[idx] = value
      ep[fieldName] = list;
      return { ...ep };
    });
  }, [])

  const handlePayload = () => {
    let payload = {};

    // basic
    payload.name = editTemplate.name;
    payload.utility_provider = editTemplate.utility_provider;

    // usage
    payload.usage_default = editTemplate.usage_default;
    payload.usage_count = editTemplate.usage_count;
    payload.usage_names = editTemplate.usage_names;

    // cost
    payload.cost_default = editTemplate.cost_default;
    payload.cost_count = editTemplate.cost_count;
    payload.cost_names = editTemplate.cost_names;

    return payload;
  };

  const handleSaveTemplate = () => {
    setIsSaving(true);
    const payload = handlePayload();
    ApiServiceServerless.post(
      `/custom_templates/${activeTemplate.id}`,
      payload,
      { authorization_id: organizationId }
    )
      .then((res) => {
        setTemplates((prev) =>
          prev.map((template) => {
            if (template['id'] !== activeTemplate.id) return template;
            return res.data;
          })
        );
        showToast('success', 'Success', `Template was updated.`);
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        throw error;
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <div style={{ minHeight: '350px', width: '100%' }}>
        {loadedAccount && (
          <>
            Loaded Account: {loadedAccount}{' '}
            <FontAwesomeIcon
              style={{
                fontSize: '18px',
                margin: '0em 0.25em',
                verticalAlign: 'text-bottom',
                cursor: 'pointer',
                color: 'var(--et_tab_grey)',
              }}
              icon={faEye}
              title={'View Statements'}
              onClick={() => setShowViewModal(true)}
            />
            <FontAwesomeIcon
              style={{
                fontSize: '18px',
                lineHeight: 'inherit',
                color: 'red',
                cursor: 'pointer',
              }}
              icon={faTimesCircle}
              title={'Clear Account'}
              disabled={isSaving}
              onClick={() => {
                setLoadedAccount(null);
                setStatements([]);
                setResults([]);
              }}
            />
          </>
        )}
        {!loadedAccount && (
          <>
            Select Account
            <FontAwesomeIcon
              style={{
                fontSize: '18px',
                margin: '0 0.35em',
                verticalAlign: 'text-bottom',
                cursor: 'pointer',
                color: 'var(--et_tab_grey)',
                opacity: '1.0',
              }}
              icon={faMeter}
              title={'Add Utility Account'}
              onClick={() => setShowSelectModal(true)}
            />
          </>
        )}

        <Tabs
          id='custom-template-tabs'
          activeKey={activeTab}
          onSelect={setActiveTab}
          className='et-second-tabs'
        >
          <Tab eventKey='template-details' title={'Template Details'}>
            <Row>
              <Col md={1}></Col>
              <Col md={10}>
                <Form>
                  <Row>
                    <Form.Group controlId='template.name' as={Col}>
                      <Form.Label>
                        Template Name
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        required
                        value={editTemplate.name || ''}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group controlId='template.utility_provider' as={Col}>
                      <Form.Label>
                        Utility Provider
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        required
                        value={editTemplate.utility_provider || ''}
                        onChange={(e) =>
                          onFieldChange('utility_provider', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group controlId='template.usage_default' as={Col}>
                      <Form.Label>
                        Use Default Usage
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        as='select'
                        required
                        value={editTemplate.usage_default}
                        onChange={(e) => {
                          onFieldChange(
                            'usage_default',
                            e.target.value === 'true' ? true : false
                          )
                          onFieldChange(
                            'usage_count',
                            e.target.value === 'true' ? null : 1
                          )
                          onFieldChange(
                            'usage_names',
                            e.target.value === 'true' ? null : ['']
                          )
                        }
                        }
                      >
                        <option key={`location-opt-true`} value={true}>
                          True
                        </option>
                        <option key={`location-opt-false`} value={false}>
                          False
                        </option>
                      </Form.Control>
                    </Form.Group>

                    <Form.Group controlId='template.cost_default' as={Col}>
                      <Form.Label>
                        Use Default Cost
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        as='select'
                        required
                        value={editTemplate.cost_default}
                        onChange={(e) => {
                          onFieldChange(
                            'cost_default',
                            e.target.value === 'true' ? true : false
                          )
                          onFieldChange(
                            'cost_count',
                            e.target.value === 'true' ? null : 1
                          )
                          onFieldChange(
                            'cost_names',
                            e.target.value === 'true' ? null : ['']
                          )
                        }
                        }

                      >
                        <option key={`location-opt-true`} value={true}>
                          True
                        </option>
                        <option key={`location-opt-false`} value={false}>
                          False
                        </option>
                      </Form.Control>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col>
                      {!editTemplate.usage_default &&
                        <Form.Group controlId='template.usage_count' >
                          <Form.Label>
                            Usage Fields (#)
                            <span className='text-warning font-weight-bold pl-1'>
                              *
                            </span>
                          </Form.Label>
                          <Form.Control
                            as='select'
                            required
                            value={editTemplate.usage_count}
                            disabled={templateInUse}
                            onChange={(e) =>
                              handleChangeCount(
                                'usage_count', 'usage_names',
                                parseInt(e.target.value)
                              )
                            }
                          >
                            {NAME_COUNT_OPTIONS.map((opt) =>
                              <option key={`usage-opt${opt}`} value={opt}>
                                {opt}
                              </option>
                            )}
                          </Form.Control>

                        </Form.Group>}
                    </Col>
                    <Col>
                      {!editTemplate.cost_default &&
                        <Form.Group controlId='template.cost_count'>
                          <Form.Label>
                            Cost Fields (#)
                            <span className='text-warning font-weight-bold pl-1'>
                              *
                            </span>
                          </Form.Label>
                          <Form.Control
                            as='select'
                            required
                            value={editTemplate.cost_count}
                            disabled={templateInUse}
                            onChange={(e) => handleChangeCount(
                              'cost_count', 'cost_names',
                              parseInt(e.target.value)
                            )}
                          >
                            {NAME_COUNT_OPTIONS.map((opt) =>
                              <option key={`cost-opt${opt}`} value={opt}>
                                {opt}
                              </option>
                            )}
                          </Form.Control>
                        </Form.Group>}
                    </Col>
                  </Row>
                  {NAME_COUNT_OPTIONS.map((opt, idx) =>
                    <Row key={idx}>
                      <Col>{(editTemplate.usage_count && parseInt(editTemplate.usage_count) >= parseInt(opt)) && <Form.Group as={Col}>
                        <Form.Label>
                          Usage Field {idx + 1}
                        </Form.Label>
                        <Form.Control
                          required
                          value={editTemplate.usage_names[idx]}
                          disabled={templateInUse}
                          onChange={(e) => editFieldName('usage_names', idx, e.target.value)}
                        />
                      </Form.Group>}</Col>
                      <Col>{(editTemplate.cost_count && parseInt(editTemplate.cost_count) >= parseInt(opt)) && <Form.Group as={Col}>
                        <Form.Label>
                          Cost Field {idx + 1}
                        </Form.Label>
                        <Form.Control
                          required
                          value={editTemplate.cost_names[idx]}
                          disabled={templateInUse}
                          onChange={(e) => editFieldName('cost_names', idx, e.target.value)}
                        />
                      </Form.Group>}</Col>
                    </Row>
                  )}
                  {templateInUse &&
                    <Alert variant='warning'>
                      Usage and Cost attributes cannot be edited while template is in use.
                    </Alert>}



                  <div style={{ width: '100%' }}>
                    <Button
                      disabled={isSaving}
                      onClick={() => handleSaveTemplate()}
                      className='float-right'
                    >
                      Save
                    </Button>
                    <Button
                      disabled={isSaving}
                      onClick={() => setEditTemplate({ ...localTemplate })}
                      className='float-right'
                      variant='outline-danger'
                      style={{ marginLeft: '10px', marginRight: '10px' }}
                    >
                      Clear Changes
                    </Button>
                  </div>
                </Form>
                <Col md={1}></Col>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey='template-usages' title={'Usages'}>
            <PlugCustomTemplateUsageRules
              activeTemplate={activeTemplate}
              setTemplates={setTemplates}
              organizationId={organizationId}
              showToast={showToast}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
            />
          </Tab>

          <Tab eventKey='template-costs' title={'Costs'}>
            <PlugCustomTemplateCostRules
              activeTemplate={activeTemplate}
              setTemplates={setTemplates}
              organizationId={organizationId}
              showToast={showToast}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
            />
          </Tab>
        </Tabs>
      </div >
      <SelectPlugLoadedAccountModal
        show={showSelectModal}
        onHide={() => {
          setShowSelectModal(false);
        }}
        providerAccounts={providerAccounts}
        organizationId={organizationId}
        setLoadedAccount={setLoadedAccount}
        setStatements={setStatements}
      />
      <ViewPlugLineItemsModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        activeTemplateId={activeTemplate.id}
        usageNames={activeTemplate.usage_names}
        costNames={activeTemplate.cost_names}
        organizationId={organizationId}
        statements={statements}
        statementIdx={statementIdx}
        setStatementIdx={setStatementIdx}
        accIdx={accIdx}
        setAccIdx={setAccIdx}
        meterIdx={meterIdx}
        setMeterIdx={setMeterIdx}
        results={results}
        setResults={setResults}
      />
    </>
  );
}
